import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Code, Eye, EyeOff } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface TrackingCode {
  id: number;
  name: string;
  code: string;
  position: 'head' | 'body';
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
}

interface TrackingCodeForm {
  name: string;
  code: string;
  position: 'head' | 'body';
  isActive: boolean;
  description: string;
}

export default function AdminTrackingCodes() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<TrackingCode | null>(null);
  const [showCodePreview, setShowCodePreview] = useState<{ [key: number]: boolean }>({});
  const [formData, setFormData] = useState<TrackingCodeForm>({
    name: '',
    code: '',
    position: 'head',
    isActive: true,
    description: ''
  });

  // Fetch tracking codes
  const { data: trackingCodes = [], isLoading, error } = useQuery({
    queryKey: ['/api/admin/tracking-codes'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/tracking-codes');
      return response.json() as Promise<TrackingCode[]>;
    }
  });

  // Create tracking code mutation
  const createMutation = useMutation({
    mutationFn: async (data: TrackingCodeForm) => {
      const response = await apiRequest('POST', '/api/admin/tracking-codes', data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tracking-codes'] });
      setIsDialogOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Tracking code created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error", 
        description: error.message || "Failed to create tracking code",
        variant: "destructive",
      });
    }
  });

  // Update tracking code mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: TrackingCodeForm }) => {
      const response = await apiRequest('PUT', `/api/admin/tracking-codes/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tracking-codes'] });
      setIsDialogOpen(false);
      setEditingCode(null);
      resetForm();
      toast({
        title: "Success",
        description: "Tracking code updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update tracking code",
        variant: "destructive",
      });
    }
  });

  // Delete tracking code mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/admin/tracking-codes/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tracking-codes'] });
      toast({
        title: "Success",
        description: "Tracking code deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete tracking code",
        variant: "destructive",
      });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      position: 'head',
      isActive: true,
      description: ''
    });
  };

  const handleEdit = (code: TrackingCode) => {
    setEditingCode(code);
    setFormData({
      name: code.name,
      code: code.code,
      position: code.position,
      isActive: code.isActive,
      description: code.description || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCode) {
      updateMutation.mutate({ id: editingCode.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this tracking code?')) {
      deleteMutation.mutate(id);
    }
  };

  const toggleCodePreview = (id: number) => {
    setShowCodePreview(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openCreateDialog = () => {
    setEditingCode(null);
    resetForm();
    setIsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load tracking codes. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tracking Codes</h1>
          <p className="text-muted-foreground mt-1">
            Manage Google Tag Manager and other tracking scripts
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Tracking Code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingCode ? 'Edit Tracking Code' : 'Add New Tracking Code'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Google Tag Manager"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="position">Position *</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value: 'head' | 'body') => setFormData(prev => ({ ...prev, position: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="head">Head Section</SelectItem>
                      <SelectItem value="body">Body Section</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="code">Code *</Label>
                <Textarea
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="Paste your tracking code here..."
                  className="min-h-32 font-mono text-sm"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description for this tracking code"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingCode ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {trackingCodes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No tracking codes yet</h3>
              <p className="text-muted-foreground mb-4">
                Add your first tracking code to start monitoring your website analytics.
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tracking Code
              </Button>
            </CardContent>
          </Card>
        ) : (
          trackingCodes.map((trackingCode: TrackingCode) => (
            <Card key={trackingCode.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CardTitle className="text-lg">{trackingCode.name}</CardTitle>
                    <Badge variant={trackingCode.isActive ? "default" : "secondary"}>
                      {trackingCode.isActive ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline">
                      {trackingCode.position === 'head' ? 'Head' : 'Body'}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCodePreview(trackingCode.id)}
                    >
                      {showCodePreview[trackingCode.id] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(trackingCode)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(trackingCode.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {trackingCode.description && (
                  <p className="text-muted-foreground mb-4">{trackingCode.description}</p>
                )}
                
                {showCodePreview[trackingCode.id] && (
                  <div className="bg-muted p-4 rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Code Preview:</h4>
                    <pre className="text-xs overflow-x-auto bg-background p-3 rounded border">
                      <code>{trackingCode.code}</code>
                    </pre>
                  </div>
                )}

                <div className="text-sm text-muted-foreground mt-4">
                  Created: {new Date(trackingCode.createdAt).toLocaleDateString()} • 
                  Updated: {new Date(trackingCode.updatedAt).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {trackingCodes.length > 0 && (
        <Alert>
          <Code className="h-4 w-4" />
          <AlertDescription>
            <strong>Note:</strong> Tracking codes marked as "Active" will be automatically injected into your website. 
            Head codes are inserted in the &lt;head&gt; section, while body codes are inserted at the end of the &lt;body&gt; section.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}